import React, { useState, useEffect, useContext } from "react";
import { Chess } from "chess.js";
import "./chess.css";
import { UserInfoContext } from "../../../Providers/theData";
import { userGameRewards } from "../../../apis/user";

// Chess piece Unicode symbols
const PIECES = {
  w: {
    p: "♟︎", // Using filled pawns with variation selector for white
    r: "♜︎", // Using filled rooks with variation selector for white
    n: "♞︎", // Using filled knights with variation selector for white
    b: "♝︎", // Using filled bishops with variation selector for white
    q: "♛︎", // Using filled queens with variation selector for white
    k: "♚︎", // Using filled kings with variation selector for white
  },
  b: {
    p: "♟",
    r: "♜",
    n: "♞",
    b: "♝",
    q: "♛",
    k: "♚",
  },
};

const ChessGame = () => {
  const { userDetails, updateUserInfo, watchScreen, updatewatchScreenInfo } =
    useContext(UserInfoContext);
  const [game, setGame] = useState(new Chess());
  const [board, setBoard] = useState([]);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [status, setStatus] = useState("");
  const [gameOver, setGameOver] = useState(false);
  const [capturedPieces, setCapturedPieces] = useState({ w: [], b: [] });
  const [points, setPoints] = useState(0);
  const [aiThinking, setAiThinking] = useState(false);
  const [movablePieces, setMovablePieces] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [showGameOverPopup, setShowGameOverPopup] = useState(false);
  const [gameResult, setGameResult] = useState(null);
  const [popupShown, setPopupShown] = useState(false);
  const [gameStatus, setGameStatus] = useState("playing");

  // Initialize the board
  useEffect(() => {
    updateBoard();
  }, []);

  // Update movable pieces whenever the game state changes
  useEffect(() => {
    if (board.length > 0) {
      updateMovablePieces();
    }
  }, [game, board, gameOver]);

  // Show popup when game status becomes finished (after API call completes)
  useEffect(() => {
    if (gameStatus === "finished") {
      setTimeout(() => {
        setShowGameOverPopup(true);
      }, 1000);
    }
  }, [gameStatus]);

  // Update the board state from the chess.js instance
  const updateBoard = (currentGame = game) => {
    console.log(
      "🎯 CHESS UPDATE BOARD - Updating board state with game:",
      currentGame.fen()
    );

    const newBoard = [];
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        const square = String.fromCharCode(97 + j) + (8 - i);
        const piece = currentGame.get(square);
        newBoard.push({
          square,
          piece,
          row: i,
          col: j,
        });
      }
    }

    console.log("🎯 CHESS UPDATE BOARD - New board created, setting state");
    setBoard(newBoard);

    // Only update game status if not in reset mode
    if (
      currentGame.fen() !==
      "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
    ) {
      updateGameStatus(currentGame);
    } else {
      console.log(
        "🎯 CHESS UPDATE BOARD - Initial board position, skipping game status update"
      );
    }

    // Track captured pieces with the new board immediately
    trackCapturedPieces(newBoard);
  };

  // Update which pieces can be moved
  const updateMovablePieces = () => {
    // Only calculate movable pieces if it's the player's turn (white)
    if (game.turn() === "w" && !gameOver) {
      const squares = [];
      // Get the current board state directly from chess.js
      const currentBoard = game.board();

      // Loop through all squares on the board
      for (let i = 0; i < 8; i++) {
        for (let j = 0; j < 8; j++) {
          const piece = currentBoard[i][j];
          // Check if there's a white piece on this square
          if (piece && piece.color === "w") {
            // Convert board coordinates to algebraic notation (e.g., "e4")
            const square = String.fromCharCode(97 + j) + (8 - i);
            // Check if this piece has any legal moves
            const moves = game.moves({
              square: square,
              verbose: true,
            });
            if (moves.length > 0) {
              squares.push(square);
            }
          }
        }
      }
      console.log("Movable squares:", squares);
      setMovablePieces(squares);
    } else {
      console.log("No movable pieces - not player's turn or game over");
      setMovablePieces([]);
    }
  };

  // Track captured pieces
  const trackCapturedPieces = (currentBoard = board) => {
    const whitePieces = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };
    const blackPieces = { p: 8, n: 2, b: 2, r: 2, q: 1, k: 1 };

    // Count remaining pieces on the board
    currentBoard.forEach((square) => {
      if (square.piece) {
        if (square.piece.color === "w") {
          whitePieces[square.piece.type]--;
        } else {
          blackPieces[square.piece.type]--;
        }
      }
    });

    // Convert to arrays of captured pieces
    const whiteCaptured = [];
    const blackCaptured = [];

    Object.entries(whitePieces).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        blackCaptured.push(type);
      }
    });

    Object.entries(blackPieces).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        whiteCaptured.push(type);
      }
    });

    setCapturedPieces({
      w: whiteCaptured,
      b: blackCaptured,
    });
  };

  // Update game status
  const updateGameStatus = (currentGame = game) => {
    if (currentGame.isCheckmate() && !popupShown) {
      const winner = currentGame.turn() === "w" ? "Black" : "White";
      setStatus(`${winner} wins!`);
      setGameOver(true);
      setGameResult(winner === "White" ? "win" : "lose");
      setPopupShown(true);

      // Award points for winning
      const winPoints = userDetails?.earlyUsersLimitReached ? 1000 : 2000;
      setPoints(winPoints);
      handleApiResponse(winPoints.toString());
    } else if (currentGame.isDraw() && !popupShown) {
      setStatus("Game over! It's a draw");
      setGameOver(true);
      setGameResult("draw");
      setPopupShown(true);

      // No points for draw
      const drawPoints = 0;
      setPoints(drawPoints);
      handleApiResponse(drawPoints.toString());
    } else if (currentGame.isCheck()) {
      setStatus(`${currentGame.turn() === "w" ? "White" : "Black"} to move`);
    } else {
      setStatus(`${currentGame.turn() === "w" ? "White" : "Black"} to move`);
    }
  };

  // Handle API response for points
  const handleApiResponse = async (points) => {
    updateUserInfo((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const apiData = {
        gamePoints: points,
        gameType: "Chess",
      };
      const res = await userGameRewards(apiData);
      if (res) {
        updateUserInfo((prev) => ({
          ...prev,
          isLoading: false,
        }));
        updatewatchScreenInfo((prev) => ({
          ...prev,
          LoginMessage: res?.message,
        }));
        // Set game status to finished only after API call completes
        setGameStatus("finished");
        console.log(
          "🎯 CHESS API CALL - Game status set to finished after API call"
        );
      }
    } catch (error) {
      console.error("API call failed:", error);
      updateUserInfo((prev) => ({
        ...prev,
        isLoading: false,
      }));
      // Set game status to finished even if API call fails
      setGameStatus("finished");
      console.log(
        "🎯 CHESS API CALL - Game status set to finished after API call failure"
      );
    }
  };

  // Handle square click
  const handleSquareClick = (square) => {
    if (gameOver || aiThinking || game.turn() === "b") return; // Only allow white moves when it's white's turn

    // If a square is already selected
    if (selectedSquare) {
      // Try to make a move
      try {
        const move = game.move({
          from: selectedSquare,
          to: square.square,
          promotion: "q", // Always promote to queen for simplicity
        });

        if (move) {
          // Award points for capturing pieces
          if (move.captured) {
            const capturePoints = getPieceValue(move.captured);
            setPoints((prev) => prev + capturePoints);

            // Update global points
            if (watchScreen) {
              updatewatchScreenInfo({
                ...watchScreen,
                tapPoints: watchScreen.tapPoints + capturePoints,
              });
            }
          }

          // Track the player's move
          setLastMove({
            from: move.from,
            to: move.to,
            color: "w",
          });

          updateBoard();
          setSelectedSquare(null);
          setValidMoves([]);
          setMovablePieces([]);

          // AI move
          setTimeout(() => makeAIMove(), 500);
        } else {
          // Invalid move, just update selection
          updateSelection(square);
        }
      } catch (e) {
        // Invalid move, just update selection
        updateSelection(square);
      }
    } else {
      // No square selected yet, select this one if it has a piece
      updateSelection(square);
    }
  };

  // Update square selection
  const updateSelection = (square) => {
    if (square.piece && square.piece.color === "w") {
      setSelectedSquare(square.square);

      // Find valid moves
      const moves = game.moves({
        square: square.square,
        verbose: true,
      });

      setValidMoves(moves.map((move) => move.to));
    } else {
      setSelectedSquare(null);
      setValidMoves([]);
    }
  };

  // Get piece value for points calculation
  const getPieceValue = (piece) => {
    switch (piece) {
      case "p":
        return 1;
      case "n":
        return 3;
      case "b":
        return 3;
      case "r":
        return 5;
      case "q":
        return 9;
      default:
        return 0;
    }
  };

  // Make AI move
  const makeAIMove = () => {
    if (gameOver || game.turn() !== "b") return;

    setAiThinking(true);

    setTimeout(() => {
      // Get all possible moves
      const moves = game.moves({ verbose: true });

      if (moves.length > 0) {
        // Prioritize captures and checks
        const captureMoves = moves.filter((move) => move.captured);
        const checkMoves = moves.filter((move) => {
          const gameCopy = new Chess(game.fen());
          gameCopy.move(move);
          return gameCopy.isCheck();
        });

        let selectedMove;

        // First priority: capturing high-value pieces
        if (captureMoves.length > 0) {
          captureMoves.sort(
            (a, b) => getPieceValue(b.captured) - getPieceValue(a.captured)
          );
          selectedMove = captureMoves[0];
        }
        // Second priority: check moves
        else if (checkMoves.length > 0) {
          selectedMove =
            checkMoves[Math.floor(Math.random() * checkMoves.length)];
        }
        // Last resort: random move
        else {
          selectedMove = moves[Math.floor(Math.random() * moves.length)];
        }

        // Make the move
        const move = game.move(selectedMove);

        // Track the AI's move
        setLastMove({
          from: move.from,
          to: move.to,
          color: "b",
        });

        updateBoard();
        // updateMovablePieces is now handled by the useEffect
      }

      setAiThinking(false);
    }, 500);
  };

  // Reset the game
  const resetGame = () => {
    console.log("🎯 CHESS RESET - Resetting game state");

    // Create new game instance
    const newGame = new Chess();

    // Reset all state variables immediately
    setGame(newGame);
    setSelectedSquare(null);
    setValidMoves([]);
    setGameOver(false);
    setPoints(0);
    setLastMove(null);
    setShowGameOverPopup(false);
    setGameResult(null);
    setPopupShown(false);
    setStatus("White to move");
    setCapturedPieces({ w: [], b: [] });
    setMovablePieces([]);
    setAiThinking(false);
    setGameStatus("playing");

    // Force immediate board update with new game instance
    console.log("🎯 CHESS RESET - Immediately updating board with new game");

    // Create fresh board array
    const newBoard = [];
    for (let i = 0; i < 8; i++) {
      for (let j = 0; j < 8; j++) {
        const square = String.fromCharCode(97 + j) + (8 - i);
        const piece = newGame.get(square);
        newBoard.push({
          square,
          piece,
          row: i,
          col: j,
        });
      }
    }

    // Set board directly
    setBoard(newBoard);
    console.log("🎯 CHESS RESET - Board reset completed");
  };

  // Go back to games menu
  const goToGamesMenu = () => {
    updateUserInfo((prev) => ({
      ...prev,
      isPlay: false,
      currentComponent: require("../../Task/Task").default,
      currentComponentText: "task",
      lastComponent: userDetails?.currentComponent,
      lastComponentText: userDetails?.currentComponentText,
      isMenu: true,
      menuCount: userDetails?.menuCount + 1,
    }));
  };

  return (
    <div className="chess-container">
      <div className="chess-game-header">
        <button
          style={{ fontSize: "18px" }}
          onClick={() => {
            updateUserInfo((prev) => ({
              ...prev,
              isPlay: true,
              currentComponent: require("./chessWeb").default,
              currentComponentText: "chessWeb",
              lastComponent: userDetails?.userDetails.currentComponent,
              lastComponentText: userDetails?.userDetails.currentComponentText,
            }));
          }}
          className="game-back-button"
        >
          ←
        </button>
        <h2
          className="chess-logo-text text-center text-uppercase w-100"
          style={{ fontSize: "20px", width: "100%" }}
        >
          Play with AI
        </h2>
      </div>

      <div className="chess-header">
        <div className="chess-game-info">
          <div className="chess-game-status">{status}</div>
          {/* {aiThinking && <div>AI is thinking...</div>} */}
        </div>

        <button
          onClick={resetGame}
          className="chess-restart-button"
          title="Restart Game"
        >
          🔄
        </button>

        {/* <div className="chess-points">
          <img
            src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
            alt="Points"
          />
          <span>{points}</span>
        </div> */}
      </div>

      {/* AI Captured Pieces - Top of Board */}
      <div className="captured-pieces ai-captured">
        <div>
          <strong className="text-uppercase">Captured by AI: </strong>
          <div className="captured-pieces-row">
            {capturedPieces.w.map((piece, index) => (
              <span key={index} className="captured-piece">
                {PIECES.b[piece]}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="chess-board">
        {board.map((square, index) => (
          <div
            key={index}
            className={`chess-square ${
              (square.row + square.col) % 2 === 0
                ? "chess-square-white"
                : "chess-square-black"
            } ${
              selectedSquare === square.square ? "chess-square-selected" : ""
            } ${
              validMoves.includes(square.square)
                ? "chess-square-valid-move"
                : ""
            } ${
              lastMove && lastMove.from === square.square
                ? "last-move-from"
                : ""
            } ${
              lastMove && lastMove.to === square.square ? "last-move-to" : ""
            }`}
            onClick={() => handleSquareClick(square)}
          >
            {square.piece && (
              <div
                className={`chess-piece ${
                  square.piece.color === "w" ? "white-piece" : "black-piece"
                } ${
                  movablePieces.includes(square.square) ? "movable-piece" : ""
                }`}
                data-is-movable={
                  movablePieces.includes(square.square) ? "true" : "false"
                }
              >
                {PIECES[square.piece.color][square.piece.type]}
              </div>
            )}
          </div>
        ))}
        {(game.isCheck() || game.isCheckmate()) && (
          <div className="chess-status-overlay">
            <div className="chess-status-message">
              {game.isCheckmate() ? "CHECKMATE!" : "CHECK!"}
            </div>
          </div>
        )}
      </div>

      {/* Player Captured Pieces - Bottom of Board */}
      <div className="captured-pieces player-captured">
        <div>
          <strong className="text-uppercase">Captured by You: </strong>
          <div className="captured-pieces-row">
            {capturedPieces.b.map((piece, index) => (
              <span key={index} className="captured-piece">
                {PIECES.w[piece]}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Game Over Popup */}
      {showGameOverPopup && (
        <div className="chess-game-overlay">
          <div className="chess-game-popup">
            <div className="chess-game-popup-content">
              <h2 className="chess-game-epic">GAME OVER!</h2>
              <div className="row text-center">
                <div className="col-12">
                  <div
                    className="chess-game-epic-div"
                    style={{ padding: "30px 40px" }}
                  >
                    <p className="chess-game-points-label">TOKENS EARNED</p>
                    <div className="chess-game-points-value">
                      <img
                        src="https://d1ccopf5cw1hft.cloudfront.net/s3data/coinlogo.png"
                        alt="logo"
                      />
                      <p className="chess-game-points-number">{points}</p>
                    </div>
                    <p className="chess-game-winner-text">
                      {gameResult === "win"
                        ? "Winner : You"
                        : gameResult === "draw"
                        ? "Game Draw!"
                        : "Winner : AI"}
                    </p>
                  </div>
                  <p className="chess-game-bottom-text text-uppercase">
                    Play daily to boost your score and rack up more Tokens!
                  </p>
                </div>
              </div>
            </div>
            <div className="chess-game-playagain">
              <button className="chess-game-playgame" onClick={resetGame}>
                PLAY AGAIN
              </button>
              <button className="chess-game-playgame" onClick={goToGamesMenu}>
                BACK TO MENU
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChessGame;
